import { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { api, downloadPdf, formatApiErrorDetail } from '@/lib/api';
import { INV } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { FileDown, RefreshCw, ReceiptText, Trash2, FileText, ChevronRight, ChevronDown, ShieldCheck, RotateCcw, AlertCircle, Eye } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import FinvoicePreviewModal from '@/components/FinvoicePreviewModal';
import HealthCheckModal from '@/components/HealthCheckModal';

function eur(n) {
  return Number(n || 0).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
}

function kgFmt(n) {
  return Number(n || 0).toLocaleString('fi-FI', { maximumFractionDigits: 2 }) + ' kg';
}

function dateFi(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fi-FI');
}

// Compute the delivery date range from invoice lines.
// Returns a Finnish-formatted single date or a "a.b. – c.d.yyyy" range.
// Falls back to invoice_date when lines are missing dates.
function deliveryRange(inv) {
  const dates = (inv?.lines || [])
    .map((l) => (l && l.date_iso) || '')
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return dateFi(inv?.invoice_date);
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first === last) return dateFi(first);
  // Compact range: drop year from the first date when both share it
  const dFirst = new Date(first);
  const dLast = new Date(last);
  if (dFirst.getFullYear() === dLast.getFullYear()) {
    const shortFirst = `${dFirst.getDate()}.${dFirst.getMonth() + 1}.`;
    return `${shortFirst} – ${dateFi(last)}`;
  }
  return `${dateFi(first)} – ${dateFi(last)}`;
}

export default function Invoicing() {
  const { user } = useAuth();
  const isAdmin = user && user.role === 'admin';
  // Default range = last full week (Mon..Sun)
  const today = new Date();
  const initFrom = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const initTo = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [tab, setTab] = useState('billable'); // 'billable' | 'invoices' | 'customer'

  const [billable, setBillable] = useState([]); // [{customer, lines, totals...}]
  const [selected, setSelected] = useState(() => new Set());
  const [invoices, setInvoices] = useState([]);
  const [invoiceSelected, setInvoiceSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [healthOpen, setHealthOpen] = useState(false);

  // Asiakashaku-välilehden tila
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);

  const loadBillable = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/invoices/billable', { params: { date_from: dateFrom, date_to: dateTo } });
      setBillable(data.customers || []);
      setSelected(new Set()); // safety: default none selected — user must opt-in
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Laskuttamattomien lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/invoices', { params: { date_from: dateFrom, date_to: dateTo } });
      setInvoices(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Laskujen lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (tab === 'billable') loadBillable();
    else if (tab === 'invoices') loadInvoices();
    else if (tab === 'customer' && allCustomers.length === 0) {
      // Lataa asiakaslista ensimmäisellä kerralla
      api.get('/customers').then(({ data }) => setAllCustomers(data || []))
        .catch(() => toast.error('Asiakaslistan lataus epäonnistui'));
    }
  }, [tab, loadBillable, loadInvoices, allCustomers.length]);

  const loadCustomerHistory = useCallback(async (cid) => {
    if (!cid) return;
    setCustomerHistoryLoading(true);
    try {
      const { data } = await api.get(`/orders/by-customer/${cid}`);
      setCustomerHistory(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Historian lataus epäonnistui');
    } finally {
      setCustomerHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCustomerId) loadCustomerHistory(selectedCustomerId);
    else setCustomerHistory(null);
  }, [selectedCustomerId, loadCustomerHistory]);

  const customerMatches = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    if (!q) return allCustomers.slice(0, 40);
    return allCustomers.filter((c) => {
      const hay = `${c.nro || ''} ${c.ketju || ''} ${c.asiakas || ''} ${c.reitti || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 40);
  }, [allCustomers, customerQuery]);

  const toggleAll = (checked) => {
    if (checked) setSelected(new Set(billable.map((c) => c.customer.id)));
    else setSelected(new Set());
  };
  const toggleOne = (id) => {
    setSelected((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  const totals = useMemo(() => {
    const sel = billable.filter((c) => selected.has(c.customer.id));
    const kg = sel.reduce((a, c) => a + (c.total_kg || 0), 0);
    const net = sel.reduce((a, c) => a + (c.total_net || 0), 0);
    const vat = sel.reduce((a, c) => a + (c.total_vat || 0), 0);
    const gross = sel.reduce((a, c) => a + (c.total_gross || 0), 0);
    return { kg, net, vat, gross, count: sel.length };
  }, [billable, selected]);

  const generate = async () => {
    if (selected.size === 0) {
      toast.error('Valitse vähintään yksi asiakas');
      return;
    }
    if (!confirm(`Muodosta ${selected.size} laskua aikaväliltä ${dateFi(dateFrom)} – ${dateFi(dateTo)}?`)) return;
    try {
      const { data } = await api.post('/invoices/generate', {
        date_from: dateFrom,
        date_to: dateTo,
        customer_ids: Array.from(selected),
      });
      toast.success(`Luotu ${data.created.length} laskua${data.skipped.length ? `, ohitettu ${data.skipped.length}` : ''}`);
      setTab('invoices');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Laskujen muodostus epäonnistui');
    }
  };

  const downloadXml = async (inv) => {
    try {
      await downloadPdf(`/api/invoices/${inv.id}/finvoice.xml`, `Finvoice_${inv.invoice_no}.xml`);
      toast.success(`Finvoice ${inv.invoice_no} ladattu`);
    } catch (e) {
      toast.error(`Vienti epäonnistui: ${e.message}`);
    }
  };

  const downloadZip = async () => {
    try {
      await downloadPdf(
        `/api/invoices/export.zip?date_from=${dateFrom}&date_to=${dateTo}&only_unexported=true`,
        `Finvoice_${dateFrom}_${dateTo}.zip`,
      );
      toast.success('ZIP-vientitiedosto ladattu (vain viemättömät)');
      loadInvoices(); // refresh "Viety"-status badges
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(`Vienti epäonnistui: ${msg}`);
    }
  };

  const downloadSelectedZip = async () => {
    if (invoiceSelected.size === 0) {
      toast.error('Valitse vähintään yksi lasku');
      return;
    }
    const ids = Array.from(invoiceSelected).join(',');
    try {
      await downloadPdf(
        `/api/invoices/export-selected.zip?ids=${encodeURIComponent(ids)}`,
        `Finvoice_valitut_${invoiceSelected.size}kpl.zip`,
      );
      toast.success(`ZIP ladattu (${invoiceSelected.size} laskua)`);
      setInvoiceSelected(new Set());
      loadInvoices();
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      toast.error(`Vienti epäonnistui: ${msg}`);
    }
  };

  const toggleInvoiceSelected = (id) => {
    setInvoiceSelected((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  const toggleAllInvoices = (visibleIds) => {
    setInvoiceSelected((s) => {
      const allChecked = visibleIds.length > 0 && visibleIds.every((id) => s.has(id));
      if (allChecked) return new Set();
      return new Set(visibleIds);
    });
  };

  const bulkDeletePermanent = async () => {
    if (invoiceSelected.size === 0) {
      toast.error('Valitse vähintään yksi lasku');
      return;
    }
    const ids = Array.from(invoiceSelected);
    if (!confirm(
      `Poistetaanko ${ids.length} lasku${ids.length !== 1 ? 'a' : ''} PYSYVÄSTI?\n\n` +
      `Tätä ei voi peruuttaa. Tilaukset, jotka olivat näillä laskuilla, palautuvat tilaan "Tilaus" ja niistä voi luoda uuden laskun.`,
    )) return;
    try {
      const { data } = await api.post('/invoices/bulk-delete-permanent', { ids });
      toast.success(`${data.deleted_count} laskua poistettu pysyvästi (${data.orders_reset} tilausta palautettu)`);
      setInvoiceSelected(new Set());
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Poisto epäonnistui');
    }
  };

  const removeInvoice = async (inv) => {
    if (!confirm(`Poistetaanko lasku ${inv.invoice_no}? Tilauksien tila palautuu "Tilaus".`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success(`Lasku ${inv.invoice_no} poistettu`);
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Poisto epäonnistui');
    }
  };

  const downloadLaheteForLine = async (line, customerId) => {
    if (!line?.date_iso || !customerId) return;
    try {
      await downloadPdf(
        `/api/lahete/single/${line.date_iso}/${customerId}.pdf`,
        `Lahete_${line.lahete_no || customerId}_${line.date_iso}.pdf`,
      );
      toast.success(`Lähete ${line.lahete_no || ''} ladattu`);
    } catch (e) {
      toast.error(`Lähete ei onnistunut: ${e.message}`);
    }
  };

  const downloadFullBackup = async () => {
    try {
      const fname = await downloadPdf('/api/admin/backup.zip', 'herne_backup.zip');
      toast.success(`Varmuuskopio ladattu (${fname})`);
    } catch (e) {
      toast.error(`Varmuuskopio ei onnistunut: ${e.message}`);
    }
  };

  const restoreInvoice = async (inv) => {
    if (!confirm(`Palauta lasku ${inv.invoice_no}? Tilauksien tila muuttuu takaisin "Laskutettu".`)) return;
    try {
      await api.post(`/invoices/${inv.id}/restore`);
      toast.success(`Lasku ${inv.invoice_no} palautettu`);
      loadInvoices();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Palautus epäonnistui');
    }
  };

  const [showDeleted, setShowDeleted] = useState(false);
  useEffect(() => {
    if (tab === 'invoices') loadInvoicesWithDeleted(showDeleted);
  }, [showDeleted, tab]);

  const loadInvoicesWithDeleted = async (incl) => {
    setLoading(true);
    try {
      const { data } = await api.get('/invoices', { params: {
        date_from: dateFrom, date_to: dateTo, include_deleted: incl,
      } });
      setInvoices(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Laskujen lataus epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs tracking-[0.15em] uppercase text-[#6A6D66] font-semibold">Talous</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-[#1C1C1A]">Laskutus</h1>
          <p className="text-sm text-[#6A6D66] mt-1">Muodosta laskut valitulta aikaväliltä ja vie Finvoice 3.0 -tiedosto Suonentietoon.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2 w-full sm:w-auto">
          <div className="space-y-1 flex-1 sm:flex-none min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-[#6A6D66] font-semibold">Alkupäivä</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid={INV.dateFrom}
              className="h-10 w-full sm:w-40"
            />
          </div>
          <div className="space-y-1 flex-1 sm:flex-none min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-[#6A6D66] font-semibold">Loppupäivä</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid={INV.dateTo}
              className="h-10 w-full sm:w-40"
            />
          </div>
          <Button
            onClick={() => (tab === 'billable' ? loadBillable() : loadInvoices())}
            data-testid={INV.reloadButton}
            variant="outline"
            className="h-9 rounded-full"
          >
            <RefreshCw size={14} className="mr-1.5" />
            Päivitä
          </Button>
          {isAdmin && (
            <Button
              onClick={async () => {
                if (!confirm('Lasketaan uudelleen veroton yksikköhinta kaikille laskuille joissa vat_rate=0 tai veroton == brutto (bugi). Käyttää nykyistä ALV-asetusta (13,5% jos asetus puuttuu). Jatketaanko?')) return;
                try {
                  const r = await api.post('/invoices/admin/fix-vat-rates');
                  const n = r.data?.fixed_count || 0;
                  const rate = r.data?.vat_rate_used;
                  if (n === 0) {
                    toast.success(`Kaikki laskut olivat jo oikein (ALV ${(rate*100).toFixed(1)}%).`);
                  } else {
                    toast.success(`Korjattu ${n} laskua käyttäen ALV ${(rate*100).toFixed(1)}%. Verottomat yksikköhinnat lasketaan nyt oikein.`);
                    loadInvoices();
                  }
                } catch (e) {
                  toast.error('Korjaus epäonnistui');
                }
              }}
              data-testid="inv-fix-vat-button"
              variant="outline"
              className="h-9 rounded-full border-[#C05A3C] text-[#C05A3C] hover:bg-[#FDF6EB]"
              title="Korjaa vanhojen laskujen veroton yksikköhinta jos se on virheellisesti sama kuin brutto (ALV=13,5%)"
            >
              <RefreshCw size={14} className="mr-1.5" />
              Korjaa ALV
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={() => setHealthOpen(true)}
              data-testid="inv-health-check-button"
              variant="outline"
              className="h-9 rounded-full border-[#2A4B30] text-[#2A4B30] hover:bg-[#E8F0E9]"
              title="Tarkista kaikkien laskujen matematiikka nykyistä 2-desim Suonentieto-yhteensopivaa sääntöä vastaan"
            >
              <ShieldCheck size={14} className="mr-1.5" />
              Terveystarkistus
            </Button>
          )}
          {isAdmin && (
            <Button
              onClick={downloadFullBackup}
              data-testid="inv-backup-button"
              variant="outline"
              className="h-9 rounded-full border-[#2A4B30] text-[#2A4B30] hover:bg-[#E8F0E9]"
              title="Lataa täysi varmuuskopio kaikista tiedoista (admin)"
            >
              <ShieldCheck size={14} className="mr-1.5" />
              Varmuuskopio
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-[#E5E6E3]">
        <button
          type="button"
          onClick={() => setTab('billable')}
          data-testid={INV.tabBillable}
          className={`px-4 py-2 text-sm border-b-2 -mb-[2px] transition-colors ${
            tab === 'billable'
              ? 'border-[#2A4B30] text-[#1C1C1A] font-medium'
              : 'border-transparent text-[#6A6D66] hover:text-[#1C1C1A]'
          }`}
        >
          Laskuttamattomat
        </button>
        <button
          type="button"
          onClick={() => setTab('invoices')}
          data-testid={INV.tabInvoices}
          className={`px-4 py-2 text-sm border-b-2 -mb-[2px] transition-colors ${
            tab === 'invoices'
              ? 'border-[#2A4B30] text-[#1C1C1A] font-medium'
              : 'border-transparent text-[#6A6D66] hover:text-[#1C1C1A]'
          }`}
        >
          Laskut
        </button>
        <button
          type="button"
          onClick={() => setTab('customer')}
          data-testid="tab-customer-search"
          className={`px-4 py-2 text-sm border-b-2 -mb-[2px] transition-colors ${
            tab === 'customer'
              ? 'border-[#2A4B30] text-[#1C1C1A] font-medium'
              : 'border-transparent text-[#6A6D66] hover:text-[#1C1C1A]'
          }`}
        >
          Asiakashaku
        </button>
      </div>

      {tab === 'billable' && (
        <BillableView
          loading={loading}
          billable={billable}
          selected={selected}
          toggleAll={toggleAll}
          toggleOne={toggleOne}
          totals={totals}
          onGenerate={generate}
          onDownloadLahete={downloadLaheteForLine}
        />
      )}

      {tab === 'invoices' && (
        <InvoicesView
          loading={loading}
          invoices={invoices}
          onDownloadXml={downloadXml}
          onPreviewXml={setPreviewId}
          onDelete={removeInvoice}
          onRestore={restoreInvoice}
          onExportZip={downloadZip}
          onExportSelectedZip={downloadSelectedZip}
          onBulkDeletePermanent={bulkDeletePermanent}
          invoiceSelected={invoiceSelected}
          onToggleInvoice={toggleInvoiceSelected}
          onToggleAllInvoices={toggleAllInvoices}
          onDownloadLahete={downloadLaheteForLine}
          showDeleted={showDeleted}
          onToggleShowDeleted={setShowDeleted}
        />
      )}
      {tab === 'customer' && (
        <CustomerSearchView
          allCustomers={allCustomers}
          query={customerQuery}
          setQuery={setCustomerQuery}
          matches={customerMatches}
          selectedId={selectedCustomerId}
          setSelectedId={setSelectedCustomerId}
          history={customerHistory}
          loading={customerHistoryLoading}
          onOpenPreview={setPreviewId}
          onDownloadLahete={downloadLaheteForLine}
        />
      )}
      {previewId && (
        <FinvoicePreviewModal invoiceId={previewId} onClose={() => setPreviewId(null)} />
      )}
      {healthOpen && (
        <HealthCheckModal
          onClose={() => setHealthOpen(false)}
          onOpenPreview={(id) => { setHealthOpen(false); setPreviewId(id); }}
        />
      )}
    </div>
  );
}

function BillableView({ loading, billable, selected, toggleAll, toggleOne, totals, onGenerate, onDownloadLahete }) {
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded((s) => {
    const ns = new Set(s);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });
  // Aakkosjärjestys: ensin ketju, sitten asiakas. localeCompare('fi') tukee ä/ö/å.
  const sortedBillable = useMemo(() => {
    const key = (c) => `${c.customer?.ketju || ''} ${c.customer?.asiakas || ''}`.trim();
    return [...billable].sort((a, b) => key(a).localeCompare(key(b), 'fi', { sensitivity: 'base' }));
  }, [billable]);
  const allChecked = sortedBillable.length > 0 && selected.size === sortedBillable.length;
  if (loading) return <div className="text-sm text-[#6A6D66]">Ladataan…</div>;
  if (sortedBillable.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#E5E6E3] rounded-xl py-16 text-center">
        <p className="font-heading text-xl text-[#1C1C1A]">Ei laskuttamattomia tilauksia</p>
        <p className="text-sm text-[#6A6D66] mt-1">Valitse toinen aikaväli tai luo tilauksia Tilauskirja-sivulla.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 bg-[#FDF6EB] border border-[#E9D9B5] rounded-xl px-4 py-2.5 text-xs text-[#6A4A1B]">
        <AlertCircle size={14} className="mt-0.5 shrink-0" />
        <span>
          Listalle päätyvät vain tilaukset joiden status on <strong>Tilaus</strong>.
          Status <strong>Arvio</strong> ei muodostu laskuksi — vahvista arviot tilauksiksi Tilauskirjassa ennen laskutusta.
        </span>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[#E8F0E9] border border-[#CFE0D2] rounded-xl px-4 py-3">
        <div className="text-sm leading-relaxed">
          <span className="font-semibold text-[#1E3622]">{totals.count}</span> asiakasta valittu ·
          <span className="ml-1"><span className="font-mono font-semibold" data-testid="billable-totals-kg">{kgFmt(totals.kg)}</span></span> ·
          netto <span className="font-mono">{eur(totals.net)}</span> ·
          ALV <span className="font-mono">{eur(totals.vat)}</span> ·
          yhteensä <span className="font-mono font-semibold">{eur(totals.gross)}</span>
        </div>
        <Button
          onClick={onGenerate}
          disabled={totals.count === 0}
          data-testid={INV.generateButton}
          className="h-10 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white disabled:bg-[#E5E6E3] disabled:text-[#6A6D66] w-full sm:w-auto shrink-0"
        >
          <ReceiptText size={15} className="mr-1.5" />
          Muodosta laskut valituista
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#E5E6E3] bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F9F9F6] border-b border-[#E5E6E3] text-[#6A6D66]">
            <tr>
              <th className="px-3 py-3 w-10">
                <Checkbox
                  checked={allChecked}
                  onCheckedChange={(v) => toggleAll(!!v)}
                  data-testid={INV.billableSelectAll}
                />
              </th>
              <th className="px-1 py-3 w-6"></th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-24">Nro</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Asiakas</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-20">Rivit</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right w-28">Kg</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-24">Hintar.</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right w-32">Netto</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right w-28">ALV</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right w-32">Yhteensä</th>
            </tr>
          </thead>
          <tbody>
            {sortedBillable.map((c) => {
              const checked = selected.has(c.customer.id);
              const isOpen = expanded.has(c.customer.id);
              return (
                <Fragment key={c.customer.id}>
                  <tr
                    data-testid={`${INV.billableRowPrefix}${c.customer.id}`}
                    className={`border-b border-[#E5E6E3] transition-colors ${checked ? 'bg-[#F4F6F3]' : 'hover:bg-[#F9F9F6]'} cursor-pointer`}
                    onClick={(e) => {
                      // Don't toggle expansion when clicking the checkbox itself
                      if ((e.target.closest && e.target.closest('button')) || e.target.tagName === 'INPUT') return;
                      toggleExpand(c.customer.id);
                    }}
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleOne(c.customer.id)}
                        data-testid={`${INV.billableSelectPrefix}${c.customer.id}`}
                      />
                    </td>
                    <td className="px-1 py-2 text-[#6A6D66]">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-[#6A6D66]">{c.customer.nro}</td>
                    <td className="px-3 py-2 font-medium text-[#1C1C1A]">{c.customer.ketju} {c.customer.asiakas}</td>
                    <td className="px-3 py-2 text-[#6A6D66] font-mono text-xs">{c.lines.length}</td>
                    <td className="px-3 py-2 font-mono text-right text-[#1C1C1A]" data-testid={`billable-row-kg-${c.customer.id}`}>{kgFmt(c.total_kg)}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${c.customer.price_group === '2' ? 'bg-[#E0EBE3] text-[#2A4B30]' : 'bg-[#F4F1E5] text-[#8A6A1F]'}`}>
                        {c.customer.price_group === '1' ? '1 Kaupat' : c.customer.price_group === '2' ? '2 Tukku' : '3 Muu'}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-right">{eur(c.total_net)}</td>
                    <td className="px-3 py-2 font-mono text-right text-[#6A6D66]">{eur(c.total_vat)}</td>
                    <td className="px-3 py-2 font-mono text-right font-semibold">{eur(c.total_gross)}</td>
                  </tr>
                  {isOpen && <LineDetailsRow lines={c.lines} vatRate={c.vat_rate} colSpan={10} customerId={c.customer.id} onDownloadLahete={onDownloadLahete} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomerSearchView({
  allCustomers, query, setQuery, matches, selectedId, setSelectedId,
  history, loading, onOpenPreview, onDownloadLahete,
}) {
  const selectedCustomer = useMemo(
    () => allCustomers.find((c) => c.id === selectedId) || null,
    [allCustomers, selectedId],
  );

  return (
    <div className="space-y-4" data-testid="customer-search-view">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 space-y-1">
          <Label className="text-[10px] uppercase tracking-wider text-[#6A6D66] font-semibold">
            Hae asiakas (nro, ketju, nimi tai reitti)
          </Label>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Esim. Prisma Hyvinkää, 222711, HK…"
            data-testid="customer-search-input"
            className="h-10"
          />
        </div>
        {selectedCustomer && (
          <Button
            variant="outline"
            onClick={() => { setSelectedId(null); setQuery(''); }}
            data-testid="customer-search-clear"
            className="h-10 rounded-full"
          >
            Tyhjennä valinta
          </Button>
        )}
      </div>

      {!selectedCustomer && (
        <div className="border border-[#E5E6E3] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F5F0]">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Nro</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Ketju</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Asiakas</th>
                <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Reitti</th>
              </tr>
            </thead>
            <tbody>
              {matches.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-[#6A6D66] text-sm">
                  {query ? 'Ei osumia' : 'Ladataan asiakkaita…'}
                </td></tr>
              )}
              {matches.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className="cursor-pointer border-t border-[#E5E6E3] hover:bg-[#F4F5F0]"
                  data-testid={`customer-search-row-${c.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs text-[#6A6D66]">{c.nro || '—'}</td>
                  <td className="px-3 py-2">{c.ketju || '—'}</td>
                  <td className="px-3 py-2 font-medium">{c.asiakas || '—'}</td>
                  <td className="px-3 py-2 text-xs text-[#6A6D66]">{c.reitti || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {matches.length >= 40 && (
            <div className="px-3 py-2 text-xs text-[#6A6D66] bg-[#F4F5F0] border-t border-[#E5E6E3]">
              Näytetään ensimmäiset 40 osumaa — tarkenna hakua nähdäksesi loput.
            </div>
          )}
        </div>
      )}

      {selectedCustomer && (
        <div className="space-y-4">
          <div className="bg-[#F4F5F0] border border-[#E5E6E3] rounded-xl px-4 py-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-mono text-xs text-[#6A6D66]">#{selectedCustomer.nro}</span>
              <span className="font-medium text-lg">{selectedCustomer.asiakas}</span>
              <span className="text-sm text-[#6A6D66]">{selectedCustomer.ketju}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#E5E6E3] text-[#6A6D66]">
                {selectedCustomer.reitti || '—'}
              </span>
            </div>
            {history?.totals && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#3A4038]">
                <span><strong>{history.totals.orders_count}</strong> tilausriviä</span>
                <span>yhteensä <span className="font-mono">{kgFmt(history.totals.kg_all)}</span></span>
                <span>laskutettu <span className="font-mono">{kgFmt(history.totals.kg_billed)}</span></span>
                <span>laskuttamatta <span className="font-mono">{kgFmt(history.totals.kg_unbilled)}</span></span>
                {history.totals.gross_billed > 0 && (
                  <span>laskuja yhteensä <span className="font-mono font-semibold">{eur(history.totals.gross_billed)}</span></span>
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-sm text-[#6A6D66]">Ladataan tilaushistoriaa…</div>
          ) : !history || history.orders.length === 0 ? (
            <div className="text-sm text-[#6A6D66] p-6 text-center border border-dashed border-[#E5E6E3] rounded-xl">
              Ei tilauksia tällä asiakkaalla.
            </div>
          ) : (
            <div className="border border-[#E5E6E3] rounded-xl overflow-hidden overflow-x-auto">
              <table className="w-full text-sm" data-testid="customer-history-table">
                <thead className="bg-[#F4F5F0]">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Päivä</th>
                    <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wider text-[#6A6D66]">Kg</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Lähete</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Ostotilaus</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Tila</th>
                    <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-[#6A6D66]">Lasku</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.orders.map((o) => {
                    const inv = o.invoice;
                    const isBilled = o.status === 'Laskutettu' && inv && !inv.deleted;
                    return (
                      <tr key={o.id} className="border-t border-[#E5E6E3] hover:bg-[#FAFBF7]">
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums font-mono text-xs">{dateFi(o.date)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-mono">{kgFmt(o.maara_kg || 0)}</td>
                        <td className="px-3 py-2 text-xs">{o.lahete_no || '—'}</td>
                        <td className="px-3 py-2 text-xs font-mono">{o.ostotilaus || '—'}</td>
                        <td className="px-3 py-2 text-xs">
                          <span className={`inline-block px-2 py-0.5 rounded-full ${
                            isBilled
                              ? 'bg-[#E8F0E9] text-[#1E3622]'
                              : (o.status === 'Peruttu')
                                ? 'bg-[#FBEAE5] text-[#7A3520]'
                                : 'bg-[#FFF6E2] text-[#7A5A00]'
                          }`}>
                            {isBilled ? 'Laskutettu' : (o.status || 'Laskuttamaton')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {inv ? (
                            <div className="space-y-0.5">
                              <div className={`font-mono ${inv.deleted ? 'line-through text-[#6A6D66]' : ''}`}>
                                #{inv.invoice_no || '—'}
                              </div>
                              {inv.total_gross != null && (
                                <div className={`text-[10px] ${inv.deleted ? 'text-[#6A6D66]' : 'text-[#3A4038]'}`}>
                                  {eur(inv.total_gross)}
                                </div>
                              )}
                              {inv.deleted && (
                                <div className="text-[10px] text-[#C05A3C]">Poistettu</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-[#6A6D66]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            {o.lahete_no && (
                              <button
                                type="button"
                                onClick={() => onDownloadLahete(selectedCustomer.id, o.date)}
                                title="Lataa lähete-PDF"
                                data-testid={`hist-lahete-${o.id}`}
                                className="w-7 h-7 rounded-full bg-white border border-[#E5E6E3] text-[#6A6D66] hover:text-[#1C1C1A] hover:bg-[#F4F5F0] inline-flex items-center justify-center transition-colors"
                              >
                                <FileText size={13} />
                              </button>
                            )}
                            {inv && !inv.deleted && (
                              <button
                                type="button"
                                onClick={() => onOpenPreview(inv.id)}
                                title="Avaa Finvoice-esikatselu"
                                data-testid={`hist-preview-${inv.id}`}
                                className="w-7 h-7 rounded-full bg-white border border-[#E5E6E3] text-[#6A6D66] hover:text-[#1C1C1A] hover:bg-[#F4F5F0] inline-flex items-center justify-center transition-colors"
                              >
                                <Eye size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function InvoicesView({ loading, invoices, onDownloadXml, onPreviewXml, onDelete, onRestore, onExportZip, onExportSelectedZip, onBulkDeletePermanent, invoiceSelected, onToggleInvoice, onToggleAllInvoices, onDownloadLahete, showDeleted, onToggleShowDeleted }) {
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded((s) => {
    const ns = new Set(s);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });
  // Aakkosjärjestys asiakkaan nimen mukaan (customer_name = "ketju asiakas").
  // localeCompare('fi') tukee ä/ö/å.
  const sortedInvoices = useMemo(
    () => [...invoices].sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || '', 'fi', { sensitivity: 'base' })),
    [invoices],
  );
  if (loading) return <div className="text-sm text-[#6A6D66]">Ladataan…</div>;
  if (sortedInvoices.length === 0) {
    return (
      <div className="bg-white border border-dashed border-[#E5E6E3] rounded-xl py-16 text-center">
        <p className="font-heading text-xl text-[#1C1C1A]">Ei laskuja</p>
        <p className="text-sm text-[#6A6D66] mt-1">Muodosta laskut ”Laskuttamattomat”-välilehdellä.</p>
      </div>
    );
  }
  const totalGross = invoices.reduce((a, i) => a + (i.total_gross || 0), 0);
  // Aikavälin kokonaiskg = summa kaikkien laskujen riveistä. Vastaa
  // Laskuttamattomat-näkymän kg-yhteenvetoa, mutta valitulle aikavälille
  // jo muodostetuista laskuista (viikoittain laskutus → näet viikon kg:n).
  const totalKg = invoices.reduce(
    (a, i) => a + (i.total_kg || (i.lines || []).reduce((b, ln) => b + (Number(ln.qty_kg) || 0), 0)),
    0,
  );
  // IDs of invoices that are checkbox-eligible (not deleted)
  const selectableIds = invoices.filter((i) => !i.deleted_at).map((i) => i.id);
  const selectableCount = selectableIds.length;
  const selectedCount = invoiceSelected ? invoiceSelected.size : 0;
  const allChecked = selectableCount > 0 && selectableIds.every((id) => invoiceSelected?.has(id));
  const someChecked = selectedCount > 0 && !allChecked;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#F4F6F3] border border-[#E5E6E3] rounded-xl px-4 py-3">
        <div className="text-sm flex items-center gap-3 flex-wrap">
          <span><span className="font-semibold">{invoices.length}</span> laskua aikavälillä · <span className="font-mono font-semibold" data-testid="invoices-totals-kg">{kgFmt(totalKg)}</span> · yhteensä <span className="font-mono font-semibold">{eur(totalGross)}</span></span>
          {selectedCount > 0 && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#E8F0E9] text-[#2A4B30]">
              {selectedCount} valittu
            </span>
          )}
          {onToggleShowDeleted && (
            <button
              type="button"
              onClick={() => onToggleShowDeleted(!showDeleted)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                showDeleted ? 'bg-[#C05A3C] text-white' : 'bg-white border border-[#E5E6E3] text-[#6A6D66] hover:text-[#1C1C1A]'
              }`}
              title="Näytä myös poistetut (30 pv säilytys)"
            >
              {showDeleted ? '✓ Poistetut näkyvissä' : 'Näytä poistetut'}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={onExportSelectedZip}
            data-testid="inv-export-selected-zip-button"
            disabled={selectedCount === 0}
            className="h-9 rounded-full bg-white border border-[#2A4B30] text-[#2A4B30] hover:bg-[#E8F0E9] disabled:opacity-50 disabled:cursor-not-allowed"
            title="Vie valitut laskut yhtenä zip-tiedostona"
          >
            <FileDown size={15} className="mr-1.5" />
            Vie valitut ({selectedCount})
          </Button>
          <Button
            onClick={onBulkDeletePermanent}
            data-testid="inv-bulk-delete-permanent-button"
            disabled={selectedCount === 0}
            className="h-9 rounded-full bg-white border border-[#C05A3C] text-[#C05A3C] hover:bg-[#FBE9E2] disabled:opacity-50 disabled:cursor-not-allowed"
            title="Poistaa valitut laskut PYSYVÄSTI. Tilaukset palautuvat 'Tilaus'-tilaan."
          >
            <Trash2 size={15} className="mr-1.5" />
            Poista valitut pysyvästi ({selectedCount})
          </Button>
          <Button
            onClick={onExportZip}
            data-testid={INV.exportZipButton}
            className="h-9 rounded-full bg-[#2A4B30] hover:bg-[#1E3622] text-white"
            title="Lataa kaikki vielä viemättömät Finvoice XML:t yhtenä zip-tiedostona"
          >
            <FileDown size={15} className="mr-1.5" />
            Vie kaikki viemättömät (ZIP)
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#E5E6E3] bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F9F9F6] border-b border-[#E5E6E3] text-[#6A6D66]">
            <tr>
              <th className="px-2 py-3 w-8">
                <input
                  type="checkbox"
                  data-testid="inv-select-all-checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={() => onToggleAllInvoices && onToggleAllInvoices(selectableIds)}
                  className="cursor-pointer accent-[#2A4B30]"
                  title="Valitse / poista kaikki"
                />
              </th>
              <th className="px-1 py-3 w-6"></th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Nro</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider">Asiakas</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Laskupvm</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-36">Toimitus</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28">Eräpvm</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-20">Rivit</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider text-right w-32">Yhteensä</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-32">Viety</th>
              <th className="px-3 py-3 text-xs font-semibold uppercase tracking-wider w-28"></th>
            </tr>
          </thead>
          <tbody>
            {sortedInvoices.map((inv) => {
              const isOpen = expanded.has(inv.id);
              return (
                <Fragment key={inv.id}>
                  <tr
                    data-testid={`${INV.invoiceRowPrefix}${inv.id}`}
                    className="border-b border-[#E5E6E3] hover:bg-[#F9F9F6] transition-colors cursor-pointer"
                    onClick={(e) => {
                      if (e.target.closest && e.target.closest('button')) return;
                      if (e.target.closest && e.target.closest('input[type="checkbox"]')) return;
                      toggleExpand(inv.id);
                    }}
                  >
                    <td className="px-2 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                      {!inv.deleted_at && (
                        <input
                          type="checkbox"
                          data-testid={`inv-select-${inv.id}`}
                          checked={invoiceSelected?.has(inv.id) || false}
                          onChange={() => onToggleInvoice && onToggleInvoice(inv.id)}
                          className="cursor-pointer accent-[#2A4B30]"
                        />
                      )}
                    </td>
                    <td className="px-1 py-2 text-[#6A6D66]">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-3 py-2 font-mono text-[#1C1C1A] font-semibold">{inv.invoice_no}</td>
                    <td className="px-3 py-2 text-[#1C1C1A]">{inv.customer_name}</td>
                    <td className="px-3 py-2 text-[#6A6D66] text-xs">{dateFi(inv.invoice_date)}</td>
                    <td
                      className="px-3 py-2 text-[#1C1C1A] text-xs whitespace-nowrap"
                      data-testid={`inv-delivery-${inv.id}`}
                      title="Toimituspäivä — yhdistää kaikki laskun lähetteiden toimituspäivät. Lähetekohtainen päivä viedään lisäksi Finvoice XML:n RowDeliveryDate-kenttään."
                    >
                      {deliveryRange(inv)}
                    </td>
                    <td className="px-3 py-2 text-[#6A6D66] text-xs">{dateFi(inv.due_date)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-[#6A6D66]">{(inv.lines || []).length}</td>
                    <td className="px-3 py-2 font-mono text-right font-semibold">{eur(inv.total_gross)}</td>
                    <td className="px-3 py-2 text-xs text-[#6A6D66]">
                      {inv.deleted_at ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-[#FBEAE5] text-[#C05A3C]">
                          Poistettu
                        </span>
                      ) : inv.exported_at ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-[#E8F0E9] text-[#2A4B30]">
                          Viety
                        </span>
                      ) : (
                        <span className="text-[#6A6D66]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onPreviewXml(inv.id); }}
                          data-testid={`${INV.invoicePreviewPrefix}${inv.id}`}
                          title="Esikatsele Finvoice-XML (ei vie laskua)"
                          className="w-8 h-8 rounded-full hover:bg-[#E8F0E9] text-[#6A6D66] hover:text-[#2A4B30] flex items-center justify-center transition-colors"
                          disabled={!!inv.deleted_at}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDownloadXml(inv); }}
                          data-testid={`${INV.invoiceXmlPrefix}${inv.id}`}
                          title="Lataa Finvoice XML"
                          className="w-8 h-8 rounded-full hover:bg-[#E8F0E9] text-[#6A6D66] hover:text-[#2A4B30] flex items-center justify-center transition-colors"
                          disabled={!!inv.deleted_at}
                        >
                          <FileText size={14} />
                        </button>
                        {inv.deleted_at ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onRestore(inv); }}
                            title="Palauta lasku"
                            className="w-8 h-8 rounded-full hover:bg-[#E8F0E9] text-[#6A6D66] hover:text-[#2A4B30] flex items-center justify-center transition-colors"
                          >
                            <RotateCcw size={14} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onDelete(inv); }}
                            data-testid={`${INV.invoiceDeletePrefix}${inv.id}`}
                            title="Poista lasku (palautettavissa 30 pv)"
                            className="w-8 h-8 rounded-full hover:bg-[#FBEAE5] text-[#6A6D66] hover:text-[#C05A3C] flex items-center justify-center transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && <LineDetailsRow lines={inv.lines || []} vatRate={inv.vat_rate} colSpan={11} customerId={inv.customer_id} onDownloadLahete={onDownloadLahete} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LineDetailsRow({ lines, vatRate, colSpan, customerId, onDownloadLahete }) {
  if (!lines || lines.length === 0) {
    return (
      <tr className="bg-[#FAFAF8]">
        <td colSpan={colSpan} className="px-12 py-4 text-sm text-[#6A6D66]">Ei rivejä</td>
      </tr>
    );
  }
  return (
    <tr className="bg-[#FAFAF8] border-b border-[#E5E6E3]">
      <td colSpan={colSpan} className="px-0 py-0">
        <div className="px-12 py-3">
          <div className="text-[10px] uppercase tracking-[0.12em] text-[#6A6D66] font-semibold mb-1.5">
            Lähetteet ({lines.length})
          </div>
          <table className="w-full text-xs">
            <thead className="text-[#6A6D66]">
              <tr className="border-b border-[#E5E6E3]">
                <th className="text-left py-1.5 w-28 font-semibold">Toimituspvm</th>
                <th className="text-left py-1.5 w-28 font-semibold">Lähete</th>
                <th className="text-left py-1.5 font-semibold">Ostotilaus</th>
                <th className="text-right py-1.5 w-20 font-semibold">Määrä</th>
                <th className="text-right py-1.5 w-24 font-semibold">À-hinta</th>
                <th className="text-right py-1.5 w-28 font-semibold">Veroton</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => (
                <tr key={`${ln.order_id || i}`} className="border-b border-[#F4F4F2] last:border-b-0">
                  <td className="py-1.5 text-[#1C1C1A]">{dateFi(ln.date_iso)}</td>
                  <td className="py-1.5 font-mono text-[#1C1C1A]">
                    {ln.lahete_no ? (
                      <button
                        type="button"
                        onClick={() => onDownloadLahete && onDownloadLahete(ln, customerId)}
                        className="text-[#2A4B30] hover:text-[#1E3622] hover:underline font-mono"
                        title="Lataa lähete-PDF uudelleen"
                      >
                        {ln.lahete_no}
                      </button>
                    ) : '—'}
                  </td>
                  <td className="py-1.5 text-[#6A6D66]">{ln.ostotilaus || '—'}</td>
                  <td className="py-1.5 text-right font-mono">{Math.round(ln.qty_kg)} kg</td>
                  <td className="py-1.5 text-right font-mono text-[#6A6D66]">{eur(ln.unit_price)}</td>
                  <td className="py-1.5 text-right font-mono font-medium">{eur(ln.line_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}
